import axios, { AxiosResponse } from 'axios'
import { IIBTokenProvider } from '@api/utils/IIBTokenProvider'
import { CountMetric, LatencyMetric, EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { useSelector } from 'react-redux'
import { getSelectedTool } from '@atredux/selectors/toolSelector'

export class IIBAjaxRequestHandler {
    /**
     * Makes the request to the appropriate endpoint. These calls come as CrossFrame calls from CrookHookCore.
     * This function sends the request to the appropriate service, inspects the response to decide if it should
     * redirect to logout and serializes the response so that the caller can use it.
     */
    public static async listener (input): Promise<string> {
        console.debug('Making request ',
            IIBAjaxRequestHandler.getDataForLoggingEvent(input.sensitive, input.data.requestData))

        const headers = JSON.parse(JSON.stringify(input.data.requestHeaders))
        // Poor man's clone; no underscore available here?

        const useMetadataResponse = input.data.requestExtra && input.data.requestExtra.useMetadataResponse || false

        const authorizationHeaderString = [
            await IIBTokenProvider.getTokenType(),
            await IIBTokenProvider.getIdToken(),
        ].join(' ')

        headers['Content-Type'] = 'application/json; charset=UTF-8'
        headers['Content-Encoding'] = 'amz-1.0'
        headers.Authorization = authorizationHeaderString

        const iibajaxRequestLatencyMetric = new LatencyMetric(EnvironmentDetail.getStoreId(), 'IIBAuth', 'IhmFlyingDutchman', 'IIBAjaxRequestHandler.Latency')
        const iibajaxRequestRequestMetric = new CountMetric(EnvironmentDetail.getStoreId(), 'IIBAuth', 'IhmFlyingDutchman', 'IIBAjaxRequestHandler.Request')
        const iibajaxRequestErrorMetric = new CountMetric(EnvironmentDetail.getStoreId(), 'IIBAuth', 'IhmFlyingDutchman', 'IIBAjaxRequestHandler.Error')
        iibajaxRequestLatencyMetric.start()
        return axios({
            method: 'POST',
            url: input.data.endpoint,
            data: input.data.requestData,
            headers,
            withCredentials: true,
        }).then(async (response: AxiosResponse) => {
            iibajaxRequestLatencyMetric.stop()
            const data = await IIBAjaxRequestHandler.processResponse(response, { useMetadataResponse })
            console.debug('Successful response',
                IIBAjaxRequestHandler.getDataForLoggingEvent(input.sensitive, data))
            iibajaxRequestRequestMetric.count(1)
            iibajaxRequestErrorMetric.count(0)
            return data
        }).catch(async (catchResponse) => {
            iibajaxRequestRequestMetric.count(0)
            iibajaxRequestErrorMetric.count(1)
            const operation = input.data.endpoint + '.Error'
            new CountMetric(EnvironmentDetail.getStoreId(), 'IIBAuth', 'IhmFlyingDutchman', operation).count(1)
            iibajaxRequestLatencyMetric.stop()
            const data = await IIBAjaxRequestHandler.processResponse(catchResponse.response, { useMetadataResponse })
            console.warn('Error response',
                IIBAjaxRequestHandler.getDataForLoggingEvent(input.sensitive, catchResponse))
            console.warn('Error data',
                IIBAjaxRequestHandler.getDataForLoggingEvent(input.sensitive, data))
            return Promise.reject(data)
        })
    }

    // private functions

    /**
     * Inspects the response to see if it should redirect to sign in. Then, it extracts the data from the response and
     * serializes it as JSON. If the response doesn't contain data, it serializes the whole response instead.
     */
    private static async processResponse (response: AxiosResponse, options: {[key: string]: any}): Promise<string> {
        const useMetadataResponse = options && options.useMetadataResponse || false

        let processedResponse = (response.hasOwnProperty('data')) ? response.data : response

        if (useMetadataResponse) {
            processedResponse = {
                metadata: {
                    hasMetadata: true,
                    statusCode: response.status,
                },
                data: processedResponse,
            }
        }

        return JSON.stringify(processedResponse)
    }

    /**
     * Returns a redacted string if `sensitive` is set to true. Otherwise it returns the data
     * (doesn't convert it to JSON).
     *
     * @param {boolean} sensitive - true if `data` contains sensitive information that shouldn't be logged.
     * @param {any} data - data that could be sensitive.
     *
     * @return {any | string} returns the `data` if `sensitivCpe` is false or a redacted string if it's `true`.
     */
    private static getDataForLoggingEvent (sensitive: boolean, data: string): string {
        if (sensitive) {
            return '<-- Redacted since sensitive=true -->'
        }
        return data
    }
}

export async function makeIIBAjaxRequestListener (message: any) {
    return IIBAjaxRequestHandler.listener(message)
}
